import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiImage, FiPlay } from "react-icons/fi";
import MediaLightbox from "./MediaLightbox";
import { projectMedia } from "./portfolioData";

const detailContent = {
  zh: {
    "property-campaign": {
      title: "保利房地產集團",
      intro: "佛山及江門 3 個在建樓盤廣告總承包，統籌項目宣傳、物料製作與現場交付。",
      meta: ["樓盤推廣", "廣告總承包", "2019.12"],
    },
    "brand-activation": {
      title: "碧桂園 · 千璽機器人",
      intro: "門店廣告及開業活動策劃，協調品牌呈現、儀式流程與現場落地。",
      meta: ["門店開業", "品牌呈現", "2019.12"],
    },
    "retail-planning": {
      title: "佛山順德區香繽廣場",
      intro: "商場廣告及活動策劃總承包，兼顧日常宣傳、戶外畫面與活動節點執行。",
      meta: ["商場推廣", "廣告物料", "2019.07"],
    },
    "government-comms": {
      title: "政府宣傳與公共資訊",
      intro: "以公共宣傳設施、戶外圍牆及室內資訊展示為主，按不同場景完成設計製作與現場交付。",
      meta: ["公共宣傳", "資訊展示", "2018-2020"],
    },
    "corporate-private-events": {
      title: "企業及私人活動",
      intro: "企業會議、品牌活動、求婚、生日派對及婚禮佈置，從方案、物料到現場的完整流程。",
      meta: ["150+ 場", "19 個獨立相冊", "8 段活動影片"],
    },
    "business-forums": {
      title: "企業孵化與峰會路演",
      intro: "組織政企對接、園區峰會、企業座談與活動路演，協助企業項目對接與落地。",
      meta: ["企業孵化", "峰會與路演", "2020-2022"],
    },
  },
  en: {
    "property-campaign": {
      title: "Poly Real Estate Group",
      intro: "Full advertising contract for three developments in Foshan and Jiangmen, coordinating campaign materials, production and delivery.",
      meta: ["Property", "Full contract", "2019.12"],
    },
    "brand-activation": {
      title: "Country Garden · Qianxi Robotics",
      intro: "Store advertising and opening-event planning, coordinating brand presentation, ceremony flow and live execution.",
      meta: ["Opening event", "Brand presentation", "2019.12"],
    },
    "retail-planning": {
      title: "Xiangbin Plaza, Shunde",
      intro: "Full retail advertising and event planning contract covering ongoing promotion, outdoor placements and campaign delivery.",
      meta: ["Retail", "Campaign materials", "2019.07"],
    },
    "government-comms": {
      title: "Government Campaigns & Public Information",
      intro: "Public campaign facilities, outdoor graphics and interior information displays designed, produced and delivered across distinct settings.",
      meta: ["Public campaigns", "Information displays", "2018-2020"],
    },
    "corporate-private-events": {
      title: "Corporate & Private Events",
      intro: "Corporate meetings, brand events, proposals, birthdays and weddings delivered from concept and materials through to the live floor.",
      meta: ["150+ events", "19 event albums", "8 video highlights"],
    },
    "business-forums": {
      title: "Business Incubation & Roadshows",
      intro: "Government liaison, park summits, enterprise forums and roadshows supporting project connections and business landing.",
      meta: ["Business incubation", "Summits & roadshows", "2020-2022"],
    },
  },
};

function GalleryGrid({ images, label, language, onOpen }) {
  const gridClass = [
    "project-media-grid",
    images.length === 1 ? "project-media-grid--single" : "",
    images.length === 2 ? "project-media-grid--pair" : "",
    images.length === 5 ? "project-media-grid--editorial" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={gridClass}>
      {images.map((image, index) => {
        const imageLabel = language === "zh" ? `${label}，圖片 ${index + 1}` : `${label}, image ${index + 1}`;
        return (
          <button
            type="button"
            className="project-media-button"
            onClick={() => onOpen(images, label, index)}
            aria-label={imageLabel}
            key={image}
          >
            <img src={image} alt={imageLabel} loading="lazy" />
            <span><FiImage aria-hidden="true" />{String(index + 1).padStart(2, "0")}</span>
          </button>
        );
      })}
    </div>
  );
}

function StandardMedia({ media, language, onOpen }) {
  const heading = media.heading[language];

  if (media.type === "gallery") {
    return (
      <section className="project-media-section site-container">
        <div className="project-media-heading">
          <h2>{heading}</h2>
          <p>{media.note[language]}</p>
        </div>
        <GalleryGrid images={media.images} label={heading} language={language} onOpen={onOpen} />
      </section>
    );
  }

  return (
    <section className="project-media-section site-container">
      <div className="project-media-heading">
        <h2>{heading}</h2>
        <p>{media.note[language]}</p>
      </div>
      <div className="project-subprojects">
        {media.sections.map((section) => (
          <article className="project-subproject" key={section.id}>
            <div className="project-subproject__heading">
              <div>
                <h3>{section.title[language]}</h3>
                <p>{section.note[language]}</p>
              </div>
              <span>{section.images.length} {language === "zh" ? "張圖片" : "images"}</span>
            </div>
            <GalleryGrid
              images={section.images}
              label={section.title[language]}
              language={language}
              onOpen={onOpen}
            />
          </article>
        ))}
      </div>
    </section>
  );
}

function EventAlbums({ media, language, activeCategory, setActiveCategory, onOpen }) {
  const currentCategory = media.categories.find((category) => category.id === activeCategory) || media.categories[0];

  return (
    <section className="event-albums site-container">
      <div className="project-media-heading">
        <h2>{media.heading[language]}</h2>
        <p>{media.note[language]}</p>
      </div>

      <div className="event-category-tabs" role="tablist" aria-label={media.heading[language]}>
        {media.categories.map((category) => (
          <button
            type="button"
            role="tab"
            aria-selected={currentCategory.id === category.id}
            className={currentCategory.id === category.id ? "is-active" : ""}
            onClick={() => setActiveCategory(category.id)}
            key={category.id}
          >
            {category.title[language]}
          </button>
        ))}
      </div>

      <div className="event-category-panel" role="tabpanel">
        <div className="event-category-intro">
          <h3>{currentCategory.title[language]}</h3>
          <p>{currentCategory.note[language]}</p>
        </div>

        {currentCategory.albums ? (
          <div className="event-album-grid">
            {currentCategory.albums.map((album) => {
              const openLabel = language === "zh" ? `開啟 ${album.title.zh}` : `Open ${album.title.en}`;
              return (
                <button
                  type="button"
                  className="event-album"
                  onClick={() => onOpen(album.images, album.title[language], album.images.indexOf(album.cover))}
                  aria-label={openLabel}
                  key={album.id}
                >
                  <div className="event-album__cover">
                    <img src={album.cover} alt={album.title[language]} loading="lazy" />
                    <span>{album.images.length} {language === "zh" ? "張" : "images"}</span>
                  </div>
                  <div className="event-album__body">
                    <h4>{album.title[language]}</h4>
                    <p>{album.note[language]}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="event-video-grid">
            {currentCategory.videos.map((video) => (
              <article className="event-video" key={video.id}>
                <div className="event-video__player">
                  <video controls preload="metadata" playsInline>
                    <source src={video.src} type="video/mp4" />
                  </video>
                </div>
                <div className="event-video__body">
                  <span><FiPlay aria-hidden="true" />{video.tag[language]}</span>
                  <h4>{video.title[language]}</h4>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProjectDetail({ language }) {
  const { slug } = useParams();
  const safeSlug = detailContent[language][slug] ? slug : "corporate-private-events";
  const project = detailContent[language][safeSlug];
  const media = projectMedia[safeSlug];
  const [activeCategory, setActiveCategory] = useState(media.type === "albums" ? media.categories[0].id : "");
  const [gallery, setGallery] = useState(null);

  useEffect(() => {
    setActiveCategory(media.type === "albums" ? media.categories[0].id : "");
    setGallery(null);
  }, [media]);

  const openGallery = (items, label, index = 0) => setGallery({ items, label, index });
  const closeGallery = () => setGallery(null);
  const showPrevious = () => setGallery((current) => ({
    ...current,
    index: (current.index - 1 + current.items.length) % current.items.length,
  }));
  const showNext = () => setGallery((current) => ({
    ...current,
    index: (current.index + 1) % current.items.length,
  }));

  return (
    <main className="project-detail-page">
      <section className="project-detail-hero site-container">
        <Link to="/project" className="back-link">
          <FiArrowLeft aria-hidden="true" />
          {language === "zh" ? "返回作品集" : "Back to portfolio"}
        </Link>
        <h1>{project.title}</h1>
        <p className="project-detail-intro">{project.intro}</p>
        <div className="project-detail-meta">
          {project.meta.map((item) => <span key={item}>{item}</span>)}
        </div>
      </section>

      {media.type === "albums" ? (
        <EventAlbums
          media={media}
          language={language}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          onOpen={openGallery}
        />
      ) : (
        <StandardMedia media={media} language={language} onOpen={openGallery} />
      )}

      <MediaLightbox
        gallery={gallery}
        language={language}
        onClose={closeGallery}
        onPrevious={showPrevious}
        onNext={showNext}
      />
    </main>
  );
}

export default ProjectDetail;
